import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import CardGiftcardRoundedIcon from "@mui/icons-material/CardGiftcardRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import LoyaltyRoundedIcon from "@mui/icons-material/LoyaltyRounded";
import NewspaperRoundedIcon from "@mui/icons-material/NewspaperRounded";
import QrCode2RoundedIcon from "@mui/icons-material/QrCode2Rounded";
import RestaurantRoundedIcon from "@mui/icons-material/RestaurantRounded";
import { QRCodeSVG } from "qrcode.react";

import { useDataStore } from "../dataStore";
import { useClientPortal } from "../clientPortal/ClientPortalContext";
import { CLIENT_PORTAL_ERROR } from "../clientPortal/clientPortalConstants";
import { toTrustedClientMediaUrl } from "../clientPortal/clientPortalMedia";
import {
  formatClientCredit,
  formatClientPortalDate,
  mergeClientOffers,
  normalizeClaims,
  normalizeClientQR,
  normalizeDirectBenefits,
  normalizeNews,
  normalizePromotions,
} from "../clientPortal/clientPortalNormalizers";
import { DEFAULT_BRAND_THEME, brandThemeCssVars } from "../themeTokens";
import OptimizedConfigImage from "../components/OptimizedConfigImage";

const EMPTY_RESOURCE = Object.freeze([]);

function replaceTokens(text, replacements = {}) {
  return Object.entries(replacements).reduce(
    (result, [key, value]) =>
      result.replaceAll(`{${key}}`, String(value ?? "")),
    String(text || "")
  );
}

function usePortalResource(
  loader,
  normalizer,
  refreshKey,
  emptyValue = EMPTY_RESOURCE,
  enabled = true
) {
  const [state, setState] = useState({
    loading: enabled,
    data: emptyValue,
    error: false,
  });

  const load = useCallback(async () => {
    if (!enabled) {
      setState({ loading: false, data: emptyValue, error: false });
      return emptyValue;
    }
    setState((current) => ({ ...current, loading: true, error: false }));
    try {
      const raw = await loader();
      const data = normalizer(raw);
      setState({ loading: false, data, error: false });
      return data;
    } catch (error) {
      setState({
        loading: false,
        data: emptyValue,
        error: true,
      });
      return null;
    }
  }, [emptyValue, enabled, loader, normalizer]);

  useEffect(() => {
    if (!enabled) {
      setState({ loading: false, data: emptyValue, error: false });
      return undefined;
    }
    let active = true;
    setState((current) => ({ ...current, loading: true, error: false }));
    loader()
      .then((raw) => {
        if (active) {
          setState({ loading: false, data: normalizer(raw), error: false });
        }
      })
      .catch(() => {
        if (active) {
          setState({
            loading: false,
            data: emptyValue,
            error: true,
          });
        }
      });
    return () => {
      active = false;
    };
  }, [emptyValue, enabled, loader, normalizer, refreshKey]);

  return { ...state, reload: load };
}

function SectionCard({
  title,
  description,
  icon,
  loading,
  error,
  empty,
  onRetry,
  loadingLabel,
  retryLabel,
  errorLabel,
  emptyLabel,
  brandTheme = DEFAULT_BRAND_THEME,
  children,
}) {
  const c = { ...DEFAULT_BRAND_THEME, ...brandTheme };

  return (
    <Card
      component="section"
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: { xs: "18px", sm: "22px" },
        border: "1px solid rgba(67,20,7,0.09)",
        bgcolor: c.surface,
        boxShadow: "0 18px 50px rgba(33,20,12,0.08)",
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.75, md: 3 } }}>
        <Stack spacing={{ xs: 1.75, sm: 2.25 }}>
          <Stack direction="row" spacing={1.35} alignItems="center">
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: "15px",
                display: "grid",
                placeItems: "center",
                bgcolor: "rgba(247,181,0,0.15)",
                color: c.text,
                border: "1px solid rgba(124,45,18,0.08)",
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                component="h2"
                variant="h6"
                fontWeight={950}
                sx={{ color: c.text, lineHeight: 1.2 }}
              >
                {title}
              </Typography>
              {description ? (
                <Typography variant="body2" color="text.secondary">
                  {description}
                </Typography>
              ) : null}
            </Box>
          </Stack>

          {loading ? (
            <Stack
              role="status"
              aria-label={`${loadingLabel}: ${title}`}
              spacing={1}
              sx={{ minHeight: 92, justifyContent: "center" }}
            >
              <Skeleton variant="rounded" height={46} sx={{ borderRadius: "14px" }} />
              <Skeleton width="72%" />
              <Typography variant="caption" color="text.secondary">
                {loadingLabel}
              </Typography>
            </Stack>
          ) : error ? (
            <Alert
              severity="error"
              variant="outlined"
              action={
                <Button color="inherit" size="small" onClick={onRetry}>
                  {retryLabel}
                </Button>
              }
              sx={{ borderRadius: "14px" }}
            >
              {errorLabel}
            </Alert>
          ) : empty ? (
            <Box
              sx={{
                minHeight: 92,
                display: "grid",
                placeItems: "center",
                textAlign: "center",
                px: 2,
                py: 2.5,
                borderRadius: "16px",
                bgcolor: "rgba(17,17,17,0.025)",
                border: "1px dashed rgba(67,20,7,0.14)",
              }}
            >
              <Typography color="text.secondary">{emptyLabel}</Typography>
            </Box>
          ) : (
            children
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

function BenefitImage({ src, alt, size = 72 }) {
  const url = toTrustedClientMediaUrl(src);
  if (!url) {
    return (
      <Box
        aria-hidden="true"
        sx={{
          width: size,
          height: size,
          minWidth: typeof size === "number" ? size : undefined,
          borderRadius: "14px",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          color: "rgba(124,45,18,0.64)",
          bgcolor: "rgba(247,181,0,0.11)",
          border: "1px solid rgba(124,45,18,0.08)",
        }}
      >
        <RestaurantRoundedIcon fontSize="small" />
      </Box>
    );
  }
  return (
    <Box
      component="img"
      src={url}
      alt={alt}
      loading="lazy"
      referrerPolicy="no-referrer"
      sx={{
        width: size,
        height: size,
        borderRadius: "14px",
        objectFit: "cover",
        flexShrink: 0,
        border: "1px solid rgba(67,20,7,0.08)",
      }}
    />
  );
}

function dateLineText(from, to, locale, t) {
  const parts = [
    from
      ? replaceTokens(t("portal.offers.from"), {
          date: formatClientPortalDate(from, locale),
        })
      : "",
    to
      ? replaceTokens(t("portal.offers.until"), {
          date: formatClientPortalDate(to, locale),
        })
      : "",
  ].filter(Boolean);
  return parts.join(" · ");
}

function DateLine({ from, to, locale, t }) {
  const text = dateLineText(from, to, locale, t);
  return text ? (
    <Typography variant="caption" color="text.secondary">
      {text}
    </Typography>
  ) : null;
}

function formatOfferValue(value, locale) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "";
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 2,
  }).format(value);
}

function isWholeOrderVoucher(offer) {
  return (
    offer.source !== "direct" &&
    offer.productId == null &&
    offer.comboId == null
  );
}

function offerDetailLines(offer, t, locale) {
  const lines = [];
  const value = formatOfferValue(offer.value, locale);
  const wholeOrderVoucher = isWholeOrderVoucher(offer);
  const hasProduct = offer.productId != null;
  const hasCombo = offer.comboId != null;

  if (offer.type === 0) {
    if (wholeOrderVoucher) {
      lines.push(t("portal.offers.fullCart"));
    } else if (hasCombo && !hasProduct) {
      if (offer.value === 0) {
        lines.push(t("portal.offers.comboBaseFree"));
      } else if (value) {
        lines.push(
          replaceTokens(t("portal.offers.comboBasePrice"), { value })
        );
      }
    } else if (offer.value === 0) {
      lines.push(t("portal.offers.free"));
    } else if (value) {
      lines.push(
        replaceTokens(t("portal.offers.fixedPrice"), {
          value,
        })
      );
    }
  } else if (offer.type === 1 && value) {
    lines.push(
      replaceTokens(t("portal.offers.fixed"), {
        value,
      })
    );
  } else if (offer.type === 2 && value) {
    lines.push(
      replaceTokens(t("portal.offers.percentage"), {
        value,
      })
    );
  }

  if (wholeOrderVoucher) {
    lines.push(t("portal.offers.voucherScope"));
  } else {
    if (offer.source === "direct") {
      lines.push(t("portal.offers.personalScope"));
    }
    if (hasProduct && hasCombo) {
      lines.push(t("portal.offers.productComboScope"));
    } else if (hasProduct) {
      lines.push(t("portal.offers.productScope"));
    } else if (hasCombo) {
      lines.push(t("portal.offers.comboScope"));
    }
  }

  const frequencyKey = {
    0: "portal.offers.useOnce",
    1: "portal.offers.onceDaily",
    3: "portal.offers.onceWeekly",
    4: "portal.offers.onceMonthly",
  }[offer.frequency];
  if (frequencyKey) lines.push(t(frequencyKey));

  if (offer.ageFrom > 0 && offer.ageTo > 0) {
    lines.push(
      replaceTokens(t("portal.offers.ages"), {
        from: offer.ageFrom,
        to: offer.ageTo,
      })
    );
  } else if (offer.ageFrom > 0) {
    lines.push(
      replaceTokens(t("portal.offers.ageFrom"), {
        age: offer.ageFrom,
      })
    );
  } else if (offer.ageTo > 0) {
    lines.push(
      replaceTokens(t("portal.offers.ageTo"), {
        age: offer.ageTo,
      })
    );
  }
  if (offer.onBirthday) lines.push(t("portal.offers.birthday"));

  return [...new Set(lines.filter(Boolean))];
}

function OfferRows({
  offers,
  claimEnabled,
  pendingId,
  onClaim,
  locale,
  t,
}) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          md: "repeat(2, minmax(0, 1fr))",
        },
        gap: 1.25,
      }}
    >
      {offers.map((offer) => {
        const pending = pendingId === offer.id;
        const canClaim =
          claimEnabled &&
          offer.source !== "direct" &&
          offer.claimable;
        const details = offerDetailLines(offer, t, locale);
        const fallbackName =
          offer.source === "direct"
            ? t("portal.offers.personalFallback")
            : t("portal.offers.fallback");
        const metadata = [
          ...new Set(
            [
              offer.description,
              ...details,
              dateLineText(offer.validFrom, offer.validTo, locale, t),
            ].filter(Boolean)
          ),
        ];

        return (
          <Stack
            key={`${offer.source}-${offer.id}`}
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 1.25, sm: 1.25 }}
            alignItems={{ sm: "center" }}
            sx={{
              p: { xs: 1.5, sm: 1.4 },
              height: "100%",
              borderRadius: "16px",
              border: "1px solid rgba(67,20,7,0.09)",
              bgcolor: "rgba(255,255,255,0.72)",
              transition: "border-color 160ms ease, box-shadow 160ms ease",
              "&:hover": {
                borderColor: "rgba(234,88,12,0.22)",
                boxShadow: "0 12px 28px rgba(33,20,12,0.07)",
              },
            }}
          >
            <BenefitImage
              src={offer.image}
              alt=""
              size={{ xs: 72, sm: 56 }}
            />
            <Stack spacing={0.4} sx={{ minWidth: 0, flex: 1 }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography
                  fontWeight={900}
                  sx={{ minWidth: 0, overflowWrap: "anywhere" }}
                >
                  {offer.name || fallbackName}
                </Typography>
                {offer.claimed ? (
                  <Chip
                    size="small"
                    color="success"
                    label={t("portal.offers.claimed")}
                    sx={{ flexShrink: 0 }}
                  />
                ) : null}
              </Stack>
              {metadata.length ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    lineHeight: 1.4,
                    overflowWrap: "anywhere",
                  }}
                >
                  {metadata.join(" · ")}
                </Typography>
              ) : null}
            </Stack>
            {canClaim ? (
              <Button
                variant="outlined"
                onClick={() => onClaim(offer.id)}
                disabled={pendingId != null}
                aria-busy={pending}
                sx={{
                  alignSelf: { xs: "stretch", sm: "center" },
                  minHeight: 44,
                  borderRadius: "13px",
                  whiteSpace: "nowrap",
                }}
              >
                {pending
                  ? t("portal.offers.claiming")
                  : t("portal.offers.claim")}
              </Button>
            ) : null}
          </Stack>
        );
      })}
    </Box>
  );
}

function getOfferGroup(offer) {
  if (offer.source === "direct") return "personal";
  if (isWholeOrderVoucher(offer)) return "voucher";
  if (offer.productId != null && offer.comboId != null) return "other";
  if (offer.productId != null) return "product";
  if (offer.comboId != null) return "combo";
  return "other";
}

function copyWithLegacyFallback(value) {
  if (typeof document === "undefined") return false;
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied =
    typeof document.execCommand === "function" &&
    document.execCommand("copy");
  document.body.removeChild(textarea);
  return copied;
}

export default function ClientAccountPage() {
  const {
    bannerUrl = "",
    logoUrl = "",
    brandName = "",
    brandTheme = DEFAULT_BRAND_THEME,
    selectedLanguageIref,
    languageCode,
    t,
  } = useDataStore();
  const c = { ...DEFAULT_BRAND_THEME, ...brandTheme };
  const {
    profile,
    claimEnabled,
    sessionVersion,
    loadQR,
    loadPromotions,
    loadClaims,
    loadDirectBenefits,
    loadNews,
    claimPromotion,
    normalizeError,
  } = useClientPortal();
  const languageIref = selectedLanguageIref || "";
  const locale = languageCode === "EN" ? "en-GB" : "ro-RO";
  const emailValidationPending = Boolean(
    profile?.emailValidationPending
  );
  const offersAvailable = !emailValidationPending;

  const normalizeQR = useCallback((raw) => normalizeClientQR(raw), []);
  const normalizePromotionRows = useCallback(
    (raw) => normalizePromotions(raw, languageIref),
    [languageIref]
  );
  const normalizeClaimRows = useCallback(
    (raw) => normalizeClaims(raw, languageIref),
    [languageIref]
  );
  const normalizeDirectRows = useCallback(
    (raw) => normalizeDirectBenefits(raw),
    []
  );
  const normalizeNewsRows = useCallback(
    (raw) => normalizeNews(raw, languageIref),
    [languageIref]
  );

  const qr = usePortalResource(
    loadQR,
    normalizeQR,
    sessionVersion,
    EMPTY_RESOURCE,
    offersAvailable
  );
  const promotions = usePortalResource(
    loadPromotions,
    normalizePromotionRows,
    sessionVersion,
    EMPTY_RESOURCE,
    offersAvailable
  );
  const claims = usePortalResource(
    loadClaims,
    normalizeClaimRows,
    sessionVersion,
    EMPTY_RESOURCE,
    offersAvailable
  );
  const direct = usePortalResource(
    loadDirectBenefits,
    normalizeDirectRows,
    sessionVersion,
    EMPTY_RESOURCE,
    offersAvailable
  );
  const news = usePortalResource(loadNews, normalizeNewsRows, sessionVersion);
  const [pendingClaimId, setPendingClaimId] = useState(null);
  const [claimMessage, setClaimMessage] = useState(null);
  const [copyStatus, setCopyStatus] = useState("");
  const reloadQR = qr.reload;

  const greeting =
    profile?.displayName || t("portal.account.fallbackName");
  const qrData = qr.data && !Array.isArray(qr.data) ? qr.data : null;
  const promotionRows = useMemo(
    () => (Array.isArray(promotions.data) ? promotions.data : []),
    [promotions.data]
  );
  const claimRows = useMemo(
    () => (Array.isArray(claims.data) ? claims.data : []),
    [claims.data]
  );
  const directRows = useMemo(
    () => (Array.isArray(direct.data) ? direct.data : []),
    [direct.data]
  );
  const offerRows = useMemo(
    () => mergeClientOffers(promotionRows, claimRows, directRows),
    [claimRows, directRows, promotionRows]
  );
  const groupedOffers = useMemo(() => {
    const groups = {
      product: [],
      combo: [],
      voucher: [],
      personal: [],
      other: [],
    };
    offerRows.forEach((offer) => {
      groups[getOfferGroup(offer)].push(offer);
    });
    return groups;
  }, [offerRows]);
  const offerGroups = [
    {
      key: "product",
      title: t("portal.offers.productGroup"),
      offers: groupedOffers.product,
    },
    {
      key: "combo",
      title: t("portal.offers.comboGroup"),
      offers: groupedOffers.combo,
    },
    {
      key: "voucher",
      title: t("portal.offers.voucherGroup"),
      offers: groupedOffers.voucher,
    },
    {
      key: "personal",
      title: t("portal.offers.personalGroup"),
      offers: groupedOffers.personal,
    },
    {
      key: "other",
      title: t("portal.offers.otherGroup"),
      offers: groupedOffers.other,
    },
  ].filter((group) => group.offers.length);
  const offersLoading =
    offerRows.length === 0 &&
    (promotions.loading || claims.loading || direct.loading);
  const offersHaveError =
    promotions.error || claims.error || direct.error;
  const points = formatClientCredit(profile?.credit, locale, {
    singular: t("portal.points.one"),
    plural: t("portal.points.many"),
    unavailable: t("portal.points.unavailable"),
  });

  useEffect(() => {
    setCopyStatus("");
  }, [qrData?.value]);

  useEffect(() => {
    if (!qrData?.expiresAt) return undefined;
    const expiry = new Date(qrData.expiresAt).getTime();
    if (!Number.isFinite(expiry)) return undefined;
    const delay = Math.min(
      Math.max(expiry - Date.now() + 1500, 1500),
      2147483647
    );
    const timer = window.setTimeout(() => {
      reloadQR();
    }, delay);
    return () => window.clearTimeout(timer);
  }, [reloadQR, qrData?.expiresAt]);

  const onCopyQR = async () => {
    if (!qrData?.value) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(qrData.value);
      } else if (!copyWithLegacyFallback(qrData.value)) {
        throw new Error("copy unavailable");
      }
      setCopyStatus("copied");
    } catch (error) {
      setCopyStatus("failed");
    }
  };

  const retryOffers = () => {
    const retries = [];
    if (promotions.error) retries.push(promotions.reload());
    if (claims.error) retries.push(claims.reload());
    if (direct.error) retries.push(direct.reload());
    return Promise.allSettled(retries);
  };

  const onClaim = async (promotionId) => {
    if (!claimEnabled || pendingClaimId != null) return;
    setPendingClaimId(promotionId);
    setClaimMessage(null);
    try {
      await claimPromotion(promotionId);
      setClaimMessage({
        severity: "success",
        text: t("portal.offers.claimSuccess"),
      });
      await Promise.allSettled([promotions.reload(), claims.reload()]);
    } catch (error) {
      const normalized = normalizeError(
        error,
        CLIENT_PORTAL_ERROR.promotionUnavailable
      );
      const alreadyClaimed =
        normalized.category === CLIENT_PORTAL_ERROR.promotionAlreadyClaimed;
      setClaimMessage({
        severity: "info",
        text: alreadyClaimed
          ? t("portal.offers.alreadyClaimed")
          : t("portal.offers.claimUnavailable"),
      });
      if (alreadyClaimed) {
        await Promise.allSettled([promotions.reload(), claims.reload()]);
      }
    } finally {
      setPendingClaimId(null);
    }
  };

  const sectionLabels = {
    loadingLabel: t("portal.common.loading"),
    retryLabel: t("portal.common.retry"),
    errorLabel: t("portal.common.sectionError"),
    emptyLabel: t("portal.common.empty"),
  };

  return (
    <Stack
      spacing={{ xs: 2.25, md: 3 }}
      sx={{
        ...brandThemeCssVars(c),
        width: "100%",
        maxWidth: 1220,
        mx: "auto",
        py: { xs: 1.5, md: 3 },
      }}
    >
      <Card
        component="section"
        elevation={0}
        sx={{
          position: "relative",
          isolation: "isolate",
          overflow: "hidden",
          color: "#fff",
          minHeight: { xs: 330, sm: 280 },
          borderRadius: { xs: "20px", sm: "28px" },
          border: "1px solid rgba(67,20,7,0.10)",
          bgcolor: c.dark,
          boxShadow: "0 26px 76px rgba(33,20,12,0.16)",
        }}
      >
        {bannerUrl ? (
          <Box
            component={OptimizedConfigImage}
            src={bannerUrl}
            alt=""
            loading="eager"
            decoding="async"
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: -3,
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        ) : null}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: -2,
            background: bannerUrl
              ? "linear-gradient(105deg, rgba(23,17,12,0.94) 0%, rgba(23,17,12,0.78) 52%, rgba(23,17,12,0.48) 100%)"
              : `linear-gradient(135deg, ${c.dark} 0%, ${c.accent} 70%, ${c.cta} 140%)`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: -1,
            background:
              "radial-gradient(circle at 88% 18%, rgba(247,181,0,0.28), transparent 34%)",
          }}
        />

        <CardContent sx={{ p: { xs: 2.5, sm: 3.5, md: 4 } }}>
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ mb: { xs: 3, sm: 4 } }}
          >
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: "16px",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                overflow: "hidden",
                bgcolor: "rgba(255,255,255,0.92)",
                border: "1px solid rgba(255,255,255,0.44)",
              }}
            >
              {logoUrl ? (
                <Box
                  component={OptimizedConfigImage}
                  src={logoUrl}
                  alt={brandName ? `${brandName} logo` : "Restaurant logo"}
                  sx={{ width: 40, height: 40, objectFit: "contain" }}
                />
              ) : (
                <AccountCircleRoundedIcon sx={{ color: c.accent }} />
              )}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              {brandName ? (
                <Typography
                  sx={{
                    color: "#fff",
                    fontWeight: 950,
                    lineHeight: 1.15,
                  }}
                  noWrap
                >
                  {brandName}
                </Typography>
              ) : null}
              <Stack
                direction="row"
                spacing={0.6}
                alignItems="center"
                sx={{ color: "rgba(255,255,255,0.72)" }}
              >
                <LockRoundedIcon sx={{ fontSize: 14 }} aria-hidden="true" />
                <Typography variant="caption" sx={{ color: "inherit", fontWeight: 800 }}>
                  {t("portal.account.title")}
                </Typography>
              </Stack>
            </Box>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 2.5, sm: 3 }}
            alignItems={{ sm: "flex-end" }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                component="h1"
                sx={{
                  color: "#fff",
                  fontWeight: 950,
                  lineHeight: 1.04,
                  fontSize: { xs: 34, sm: 44, md: 52 },
                  maxWidth: 720,
                }}
              >
                {replaceTokens(t("portal.account.greeting"), {
                  name: greeting,
                })}
              </Typography>
              <Typography
                sx={{
                  mt: 1,
                  maxWidth: 650,
                  color: "rgba(255,255,255,0.78)",
                  lineHeight: 1.55,
                }}
              >
                {t("portal.account.summary")}
              </Typography>
            </Box>
            <Box
              sx={{
                width: { xs: "100%", sm: 280 },
                p: 2.25,
                borderRadius: "20px",
                bgcolor: "rgba(255,255,255,0.92)",
                border: "1px solid rgba(255,255,255,0.44)",
                boxShadow: "0 18px 44px rgba(0,0,0,0.16)",
                backdropFilter: "blur(14px)",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <LoyaltyRoundedIcon sx={{ color: c.accent }} aria-hidden="true" />
                <Typography variant="caption" color="text.secondary" fontWeight={850}>
                  {t("portal.points.title")}
                </Typography>
              </Stack>
              <Typography variant="h5" fontWeight={950} sx={{ color: c.text }}>
                {points}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {t("portal.points.rate")}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {t("portal.points.usage")}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {emailValidationPending ? (
        <Alert severity="info" variant="outlined" sx={{ borderRadius: "16px" }}>
          {t("portal.account.emailValidationPending")}
        </Alert>
      ) : null}

      <Grid container spacing={{ xs: 2, md: 2.5 }}>
        <Grid item xs={12}>
          <SectionCard
            title={t("portal.qr.title")}
            description={t("portal.qr.description")}
            icon={<QrCode2RoundedIcon />}
            loading={qr.loading}
            error={qr.error}
            empty={!qrData}
            onRetry={reloadQR}
            brandTheme={c}
            {...sectionLabels}
            emptyLabel={
              emailValidationPending
                ? t("portal.qr.emailValidationPending")
                : t("portal.qr.empty")
            }
          >
            {qrData ? (
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={{ xs: 1.75, md: 3 }}
                alignItems="center"
              >
                <Box
                  sx={{
                    bgcolor: "#fff",
                    p: 1.75,
                    borderRadius: "18px",
                    border: "1px solid rgba(17,17,17,0.10)",
                    boxShadow: "0 16px 34px rgba(33,20,12,0.09)",
                    lineHeight: 0,
                    flexShrink: 0,
                  }}
                >
                  <QRCodeSVG
                    value={qrData.value}
                    size={184}
                    level="M"
                    marginSize={1}
                    role="img"
                    aria-label={t("portal.qr.imageLabel")}
                  />
                </Box>
                <Stack
                  spacing={1.25}
                  sx={{
                    width: "100%",
                    minWidth: 0,
                    maxWidth: 660,
                    alignItems: { xs: "center", md: "flex-start" },
                    textAlign: { xs: "center", md: "left" },
                  }}
                >
                  <Box sx={{ width: "100%" }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 0.5 }}
                    >
                      {t("portal.qr.code")}
                    </Typography>
                    <Box
                      component="code"
                      sx={{
                        display: "block",
                        p: 1.4,
                        borderRadius: "13px",
                        border: "1px solid rgba(17,17,17,0.12)",
                        bgcolor: "rgba(17,17,17,0.035)",
                        fontSize: "0.95rem",
                        lineHeight: 1.5,
                        overflowWrap: "anywhere",
                        userSelect: "text",
                      }}
                    >
                      {qrData.value}
                    </Box>
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<ContentCopyRoundedIcon fontSize="small" />}
                    onClick={onCopyQR}
                    sx={{ minHeight: 44, borderRadius: "13px" }}
                  >
                    {copyStatus === "copied"
                      ? t("portal.qr.copied")
                      : t("portal.qr.copy")}
                  </Button>
                  {copyStatus === "failed" ? (
                    <Alert severity="info" sx={{ borderRadius: "13px" }}>
                      {t("portal.qr.copyFailed")}
                    </Alert>
                  ) : null}
                  {qrData.expiresAt ? (
                    <Typography variant="body2" color="text.secondary">
                      {replaceTokens(t("portal.qr.validUntil"), {
                        date: formatClientPortalDate(qrData.expiresAt, locale),
                      })}
                    </Typography>
                  ) : null}
                </Stack>
              </Stack>
            ) : null}
          </SectionCard>
        </Grid>

        <Grid item xs={12}>
          <SectionCard
            title={t("portal.offers.title")}
            description={t("portal.offers.description")}
            icon={<CardGiftcardRoundedIcon />}
            loading={offersLoading}
            error={false}
            empty={
              !offersLoading &&
              !offersHaveError &&
              offerRows.length === 0
            }
            brandTheme={c}
            {...sectionLabels}
            emptyLabel={
              emailValidationPending
                ? t("portal.offers.emailValidationPending")
                : t("portal.offers.empty")
            }
          >
            <Stack spacing={2}>
              {claimMessage ? (
                <Alert severity={claimMessage.severity} sx={{ borderRadius: "14px" }}>
                  {claimMessage.text}
                </Alert>
              ) : null}
              {offersHaveError ? (
                <Alert
                  severity="warning"
                  action={
                    <Button color="inherit" size="small" onClick={retryOffers}>
                      {t("portal.common.retry")}
                    </Button>
                  }
                  sx={{ borderRadius: "14px" }}
                >
                  {offerRows.length
                    ? t("portal.common.partialError")
                    : t("portal.common.sectionError")}
                </Alert>
              ) : null}
              {offerGroups.map((group) => (
                <Box
                  component="section"
                  key={group.key}
                  aria-labelledby={`offer-group-${group.key}`}
                >
                  <Typography
                    id={`offer-group-${group.key}`}
                    component="h3"
                    variant="subtitle1"
                    fontWeight={950}
                    sx={{ mb: 1, color: c.text }}
                  >
                    {group.title}
                  </Typography>
                  <OfferRows
                    offers={group.offers}
                    claimEnabled={claimEnabled && offersAvailable}
                    pendingId={pendingClaimId}
                    onClaim={onClaim}
                    locale={locale}
                    t={t}
                  />
                </Box>
              ))}
            </Stack>
          </SectionCard>
        </Grid>

        <Grid item xs={12}>
          <SectionCard
            title={t("portal.news.title")}
            description={t("portal.news.description")}
            icon={<NewspaperRoundedIcon />}
            loading={news.loading}
            error={news.error}
            empty={!news.data.length}
            onRetry={news.reload}
            brandTheme={c}
            {...sectionLabels}
            emptyLabel={t("portal.news.empty")}
          >
            <Grid container spacing={2}>
              {news.data.map((item) => (
                <Grid item xs={12} md={6} key={item.id}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    sx={{
                      p: { xs: 1.5, sm: 1.75 },
                      height: "100%",
                      borderRadius: "16px",
                      border: "1px solid rgba(67,20,7,0.09)",
                      bgcolor: "rgba(255,255,255,0.72)",
                    }}
                  >
                    <BenefitImage src={item.image} alt="" />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography component="h3" fontWeight={900} sx={{ color: c.text }}>
                        {item.title || t("portal.news.fallback")}
                      </Typography>
                      {item.summary ? (
                        <Typography variant="body2" color="text.secondary">
                          {item.summary}
                        </Typography>
                      ) : null}
                      {item.details ? (
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 1,
                            whiteSpace: "pre-wrap",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {item.details}
                        </Typography>
                      ) : null}
                      <DateLine
                        from={item.validFrom}
                        to={item.validTo}
                        locale={locale}
                        t={t}
                      />
                    </Box>
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </SectionCard>
        </Grid>
      </Grid>
    </Stack>
  );
}
